import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { keys } from '@/lib/query';

export interface QueueItem {
  id: string;
  position: number;
  isCompleted: boolean;
  item: {
    id: string; name: string; values: Record<string, unknown>;
    board: { id: string; name: string };
    group: { id: string; name: string; color: string | null };
  };
}

export interface TodayQueue {
  id: string;
  date: string;
  items: QueueItem[];
}

export interface TimerStatus {
  timer: {
    id: string; startedAt: string; isPaused: boolean;
    pausedAt: string | null; totalPausedSeconds: number;
    currentQueueItem: { id: string; item: { id: string; name: string; boardId: string } } | null;
  };
  elapsedSeconds: number;
  isPaused: boolean;
  currentItem: { id: string; name: string; boardId: string } | null;
}

export const useTimerStatus = (enabled = true) =>
  useQuery({
    queryKey: keys.timer,
    queryFn: () => api.get<TimerStatus | null>('/timer/status'),
    enabled,
  });

export const useTodayQueue = () =>
  useQuery({
    queryKey: keys.queueToday,
    queryFn: () => api.get<TodayQueue>('/queue/today'),
  });

/**
 * The timer walks the day's queue, so every timer action can change the
 * queue too - both are refreshed together.
 */
export function useTimerControls() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.timer });
    void qc.invalidateQueries({ queryKey: keys.queueToday });
    void qc.invalidateQueries({ queryKey: ['boards'] });
  };

  return {
    start: useMutation({ mutationFn: () => api.post('/timer/start'), onSuccess: invalidate }),
    lap: useMutation({ mutationFn: () => api.post('/timer/lap'), onSuccess: invalidate }),
    pause: useMutation({ mutationFn: () => api.post('/timer/pause'), onSuccess: invalidate }),
    resume: useMutation({ mutationFn: () => api.post('/timer/resume'), onSuccess: invalidate }),
    stop: useMutation({
      mutationFn: (notes?: string) => api.post('/timer/stop', { notes }),
      onSuccess: invalidate,
    }),
  };
}

export function useQueueControls() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: keys.queueToday });

  return {
    add: useMutation({
      mutationFn: (itemId: string) => api.post('/queue/items', { itemId }),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (queueItemId: string) => api.delete(`/queue/items/${queueItemId}`),
      onSuccess: invalidate,
    }),
    reorder: useMutation({
      mutationFn: (queueItemIds: string[]) => api.post('/queue/reorder', { queueItemIds }),
      onSuccess: invalidate,
    }),
  };
}

export interface TimeLog {
  id: string; startedAt: string; endedAt: string | null;
  durationSeconds: number | null; notes: string | null; isManual: boolean;
  /** Null when the time was tracked without a task. */
  item: { id: string; name: string; board: { id: string; name: string } } | null;
}

export const useMyTimeLogs = (from: string, to: string) =>
  useQuery({
    queryKey: keys.myTimeLogs(from, to),
    queryFn: () => api.get<TimeLog[]>(`/time-logs/my?from=${from}&to=${to}`),
  });

export function useTimeLogActions() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['time-logs'] });

  return {
    create: useMutation({
      mutationFn: (body: { itemId: string; startedAt: string; endedAt: string; notes?: string }) =>
        api.post('/time-logs', body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api.delete(`/time-logs/${id}`),
      onSuccess: invalidate,
    }),
  };
}
